import { BaseAutoTracker } from './BaseAutoTracker';
import { GomarkePixelConfig } from '@/types';
import { AddToCartTracker } from '@/conversion/tracks/addToCart';
import type { TrackConversionFn } from '../engine';
import { CustomerDataStore, CustomerMapper } from '@/conversion/customer';

const DEDUPE_STORAGE_KEY = 'ag_customer_add_to_cart_tracked';

export interface IAutoCustomerAddToCartConfig {
  isEnabled: boolean;
}

/**
 * Dispara automaticamente o evento ADD_TO_CART existente quando email + phone
 * estão disponíveis no CustomerDataStore. Uma única vez por sessão.
 */
export class AutoCustomerAddToCartTracker extends BaseAutoTracker {
  private autoConfig?: IAutoCustomerAddToCartConfig;
  private store = CustomerDataStore.getInstance();
  private addToCartTracker: AddToCartTracker;
  private trackConversion?: TrackConversionFn;
  private unsubscribe?: () => void;
  private hasTracked = false;

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.addToCartTracker = new AddToCartTracker(
      config,
      sessionId,
      visitorId,
      version
    );
  }

  public async initialize(
    autoConfig: IAutoCustomerAddToCartConfig
  ): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoCustomerAddToCart desabilitado');
      return;
    }

    this.hasTracked = this.readDedupeFlag();
    this.autoConfig = autoConfig;
    this.log('Inicializando AutoCustomerAddToCart');

    this.unsubscribe = this.store.subscribe(() => {
      void this.checkAndTrack();
    });

    void this.checkAndTrack();
    this.isInitialized = true;
  }

  private async checkAndTrack(): Promise<void> {
    if (!this.autoConfig || this.hasTracked) return;
    if (!this.store.hasRequiredForAddToCart()) return;

    const customer = CustomerMapper.toAddToCartCustomer(this.store.get());
    if (!customer) return;

    this.hasTracked = true;
    this.markDedupeFlag();

    this.log('Email e telefone capturados – disparando ADD_TO_CART');

    const payload = {
      itemId: `customer_${this.sessionId}`,
      itemName: 'Customer Capture',
      customer,
      metadata: {
        source: 'auto_customer_tracker',
        trigger: 'email_phone_captured',
      },
    };

    if (this.trackConversion) {
      await this.trackConversion('addToCart', payload, {
        eventId: `addToCart_customer_${this.sessionId}`,
      });
      return;
    }

    await this.addToCartTracker.trackWithCustomParams(
      {
        itemId: payload.itemId,
        itemName: payload.itemName,
      },
      {
        customer,
        metadata: payload.metadata,
      }
    );
  }

  private readDedupeFlag(): boolean {
    try {
      return sessionStorage.getItem(DEDUPE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private markDedupeFlag(): void {
    try {
      sessionStorage.setItem(DEDUPE_STORAGE_KEY, '1');
    } catch {
      // ignore storage errors
    }
  }

  public stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.isInitialized = false;
    this.log('AutoCustomerAddToCart parado');
  }

  public async rebuild(): Promise<void> {
    if (this.autoConfig && !this.hasTracked) {
      void this.checkAndTrack();
    }
  }

  public isActive(): boolean {
    return this.isInitialized && !this.hasTracked;
  }

  public getAddToCartStats(): { hasTracked: boolean } {
    return { hasTracked: this.hasTracked };
  }

  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoCustomerAddToCart] ${message}`, data);
    }
  }
}
